import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Compass, Trash2, X, Sparkles, Paperclip, Maximize2, Minimize2, FileText, Languages, Loader2 } from 'lucide-react';
import { api, NetworkError, ApiErr, type CaptureResponse } from './lib/api';
import type { TimelineItem, AskItem } from './lib/types';
import { Insights } from './components/Insights';
import { AccessSettings } from './components/AccessSettings';
import { ImportConsole } from './components/ImportConsole';
import { GearMenu } from './components/GearMenu';
import { Thread } from './components/Thread';
import { BudgetAlertBanner } from './components/StatusBar';
import { AuthButton } from './components/AuthButton';
import { Help } from './components/Help';
import { Disclaimer } from './components/Disclaimer';
import { Explore } from './components/Explore';
import { CaptureIntro } from './components/CaptureIntro';
import { ThemePicker } from './components/ThemePicker';
import { MyCaptures } from './components/MyCaptures';
import { rememberCapture } from './lib/myCaptures';

type Mode = 'ask' | 'capture';
type View = 'chat' | 'explore';

const fmtSize = (b: number): string =>
  b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;

export function App() {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<View>('chat');
  const [mode, setMode] = useState<Mode>('ask');
  const [input, setInput] = useState('');
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [themes, setThemes] = useState<string[]>([]);
  const [editing, setEditing] = useState<CaptureResponse | null>(null);
  const [gapHint, setGapHint] = useState<string | null>(null);
  // Ce que la capture en cours « résout » : une question ouverte ou une
  // contradiction (écartée automatiquement une fois la réponse enregistrée).
  const [gapResolve, setGapResolve] = useState<{ kind: 'question' | 'contradiction'; id: string } | null>(null);
  const [minesKey, setMinesKey] = useState(0);
  const [help, setHelp] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canImport, setCanImport] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [meId, setMeId] = useState<string | undefined>(undefined);
  // Disclaimer « version de test » : affiché au premier login, puis rappelable.
  const [disclaimer, setDisclaimer] = useState<boolean>(() => {
    try {
      return localStorage.getItem('gnosis.disclaimer.v1') !== '1';
    } catch {
      return true;
    }
  });
  const closeDisclaimer = () => {
    setDisclaimer(false);
    try {
      localStorage.setItem('gnosis.disclaimer.v1', '1');
    } catch {
      /* stockage indisponible */
    }
  };
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Pièces jointes de la capture en cours (CAP-06) et éditeur agrandi.
  const [files, setFiles] = useState<File[]>([]);
  const [expanded, setExpanded] = useState(false);
  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    // Capter les fichiers MAINTENANT : `list` (= input.files) est vidé dès qu'on
    // remet `value = ''`, or l'updater de setFiles s'exécute plus tard (au rendu
    // suivant). Sans cette copie, la pièce jointe pouvait disparaître selon
    // l'ordre des rendus (taper le texte avant d'ajouter le fichier).
    const picked = Array.from(list);
    setFiles((cur) => [...cur, ...picked].slice(0, 8));
    if (fileRef.current) fileRef.current.value = '';
  };
  const removeFile = (i: number) => setFiles((cur) => cur.filter((_, j) => j !== i));

  // Rouvre une capture pour édition : on bascule en mode Capturer et on
  // pré-remplit le composeur avec le texte canonique + ses thèmes (CAP-05).
  const startEdit = (n: CaptureResponse) => {
    setMode('capture');
    setEditing(n);
    setGapHint(null);
    setGapResolve(null);
    setInput(n.bodyClean);
    setThemes(n.themes.map((th) => th.theme.label));
    requestAnimationFrame(() => composerRef.current?.focus());
  };
  const cancelEdit = () => {
    setEditing(null);
    setInput('');
    setThemes([]);
    setFiles([]);
  };

  // Rôle : on n'affiche le tableau de bord « Retours » qu'aux ADMIN (D).
  useEffect(() => {
    void api.auth
      .me()
      .then((s) => {
        setIsAdmin(s.user?.role === 'ADMIN');
        setCanImport(Boolean(s.user?.canImport));
        setAuthed(Boolean(s.user));
        setMeId(s.user?.id);
      })
      .catch(() => {});
  }, []);

  // Version courante affichée près de la marque (suit la build déployée).
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    void api
      .status()
      .then((s) => setVersion(s.version))
      .catch(() => {});
  }, []);

  // Traduction du brouillon avant capture (TRAD) : écrire dans sa langue maternelle,
  // puis convertir vers la langue de l'interface ; on relit avant d'enregistrer.
  const [translatingDraft, setTranslatingDraft] = useState(false);
  const translateDraft = async (): Promise<void> => {
    const text = input.trim();
    if (!text || translatingDraft) return;
    setTranslatingDraft(true);
    try {
      const r = await api.translate(text, i18n.language === 'de' ? 'de' : 'fr');
      if (r.text) setInput(r.text);
    } catch {
      /* on laisse l'utilisateur réessayer */
    }
    setTranslatingDraft(false);
  };

  const patchItem = (id: string, p: Partial<AskItem>) =>
    setItems((xs) => xs.map((x) => (x.id === id && x.kind === 'ask' ? { ...x, ...p } : x)));

  // « Réponse approfondie » (CHAT-04) : relance la même question, modèle puissant forcé.
  const deepen = async (item: AskItem) => {
    patchItem(item.id, { deepening: true });
    try {
      const resp = await api.ask(item.question, true);
      patchItem(item.id, { response: resp, deepening: false, deepened: true });
    } catch {
      patchItem(item.id, { deepening: false });
    }
  };

  // Retour 👍/👎 sur une réponse (D).
  const feedback = (item: AskItem, value: 1 | -1) => {
    patchItem(item.id, { feedback: value });
    if (item.response?.logId) void api.chatFeedback(item.response.logId, value).catch(() => {});
  };

  // « Compléter la base » depuis une réponse du chat (CHAT-03) : bascule sur la
  // capture, la question en rappel, l'utilisateur écrit la connaissance manquante.
  const completeFromChat = (question: string) => {
    setMode('capture');
    setEditing(null);
    setGapResolve(null);
    setGapHint(question);
    setInput('');
    setThemes([]);
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  // Répondre à une « question ouverte » (PRO-01) : on bascule sur la capture,
  // pré-remplie avec le sujet et son thème, la question affichée en rappel.
  const askGap = (
    subject: string,
    question: string,
    tag?: string,
    resolve?: { kind: 'question' | 'contradiction'; id: string },
  ) => {
    setView('chat');
    setMode('capture');
    setEditing(null);
    setGapHint(question);
    setGapResolve(resolve ?? null);
    setInput(subject ? `${subject} — ` : '');
    setThemes(tag ? [tag] : []);
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  // Deep-links Explorer (UI-07) : un lien #/entity|theme|grain/… ouvre directement
  // la bonne page sur l'onglet Explorer. Restaure aussi la cible après connexion
  // (le retour OIDC ramène sur l'accueil sans le hash).
  useEffect(() => {
    try {
      const ret = localStorage.getItem('gnosis.returnHash');
      if (ret) {
        localStorage.removeItem('gnosis.returnHash');
        if (ret.startsWith('#/')) location.hash = ret;
      }
    } catch {
      /* stockage indisponible */
    }
    const syncView = (): void => {
      if (/^#\/(theme|entity|grain|author)\//.test(location.hash)) setView('explore');
    };
    syncView();
    window.addEventListener('hashchange', syncView);
    return () => window.removeEventListener('hashchange', syncView);
  }, []);

  const angles = ['angleElement', 'angleProblem', 'angleSolution', 'angleOwner'] as const;
  const insertAngle = (label: string) =>
    setInput((v) => `${v}${v && !v.endsWith('\n') ? '\n' : ''}${label} : `);

  const scrollDown = () => requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: 'smooth' }));

  async function submit() {
    const text = input.trim();
    if (!text || busy) return;
    const id = crypto.randomUUID();
    const tags = themes;
    const wasEditing = editing;
    const fromResolve = gapResolve;
    const attached = files;
    setInput('');
    setThemes([]);
    setEditing(null);
    setGapHint(null);
    setGapResolve(null);
    setFiles([]);
    setExpanded(false);
    setBusy(true);

    const base: TimelineItem =
      mode === 'ask'
        ? { id, kind: 'ask', question: text, status: 'loading' }
        : { id, kind: 'capture', text, status: 'loading' };
    setItems((xs) => [...xs, base]);
    scrollDown();

    const patch = (p: Partial<TimelineItem>) =>
      setItems((xs) => xs.map((x) => (x.id === id ? ({ ...x, ...p } as TimelineItem) : x)));

    try {
      if (mode === 'ask') {
        patch({ status: 'done', response: await api.ask(text) });
      } else {
        // Réédition (CAP-05) ou capture neuve ; dans les deux cas on retient le
        // grain côté navigateur pour le retrouver dans « Tes captures ».
        const resp = wasEditing
          ? await api.updateCapture(wasEditing.id, text, tags)
          : await api.capture(text, tags, attached);
        patch({ status: 'done', response: resp });
        rememberCapture(resp.id, resp.title);
        setMinesKey((k) => k + 1);
        // Répondre/clarifier écarte la question ou la contradiction d'origine (PRO).
        if (fromResolve && !wasEditing) {
          const p =
            fromResolve.kind === 'question'
              ? api.proactive.dismissQuestion(fromResolve.id)
              : api.proactive.dismissContradiction(fromResolve.id);
          void p.catch(() => {});
        }
      }
    } catch (err) {
      const code = err instanceof NetworkError ? 'network' : err instanceof ApiErr ? err.code : 'generic';
      patch({ status: 'error', errorCode: code });
    } finally {
      setBusy(false);
      scrollDown();
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    // En mode « Demander », Entrée envoie. En « Capturer », Entrée = nouvelle ligne
    // (vrai éditeur) ; on valide avec le bouton.
    if (e.key === 'Enter' && !e.shiftKey && mode === 'ask') {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <div className="mx-auto flex h-full max-w-6xl flex-col px-4">
      <BudgetAlertBanner />
      <header className="flex flex-wrap items-center justify-between gap-2 py-3 sm:py-4">
        <button
          onClick={() => setView('chat')}
          title={t('view.chat')}
          className="order-1 flex min-w-0 items-center gap-2.5 rounded-lg text-left transition hover:opacity-80"
        >
          <img src="/icon.svg" alt="" aria-hidden="true" className="h-8 w-8 shrink-0" />
          <div className="hidden leading-tight sm:block">
            <h1 className="flex items-baseline gap-1.5 font-semibold">
              {t('app.title')}
              {/* La version porte déjà son « v » (tag vX.Y.Z) — ne pas le doubler. */}
              {version && <span className="text-xs font-normal tabular-nums text-muted">{version}</span>}
            </h1>
            <p className="text-xs text-muted">{t('app.tagline')}</p>
          </div>
        </button>

        {/* Nav principale : pleine largeur sous l'en-tête en mobile, au centre en desktop. */}
        <div className="order-3 flex basis-full justify-center sm:order-2 sm:basis-auto">
          <nav className="inline-flex rounded-xl bg-surface-2 p-1 text-sm">
            {(['chat', 'explore'] as const).map((v) => (
              <button
                key={v}
                onClick={() => {
                  setView(v);
                  if (v === 'explore') location.hash = '#/'; // l'onglet Explorer ramène à l'accueil
                }}
                aria-pressed={view === v}
                className={`rounded-lg px-4 py-1.5 font-medium transition ${
                  view === v ? 'bg-accent text-accent-fg shadow-sm' : 'text-muted hover:text-fg'
                }`}
              >
                {t(`view.${v}`)}
              </button>
            ))}
          </nav>
        </div>

        {/* Droite : identité + réglages. Tout le reste (aide, outils, langue,
            thème, solde IA, déconnexion) vit sous la roue dentée. */}
        <div className="order-2 flex items-center gap-2 sm:order-3">
          <AuthButton />
          <GearMenu
            isAdmin={isAdmin}
            canImport={canImport}
            authed={authed}
            onHelp={() => setHelp(true)}
            onInsights={() => setInsightsOpen(true)}
            onAccess={() => setAccessOpen(true)}
            onImport={() => setImportOpen(true)}
          />
        </div>
      </header>

      {view === 'explore' ? (
        <div className="flex-1 overflow-hidden pb-3">
          <Explore onCapture={askGap} />
        </div>
      ) : (
        <>
          <main ref={scrollRef} className="mx-auto w-full max-w-3xl flex-1 overflow-y-auto py-2">
            {items.length === 0 ? (
              mode === 'capture' ? (
                <CaptureIntro />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted">
                  <Compass className="h-10 w-10 text-muted" strokeWidth={1.5} />
                  <p className="font-medium text-fg">{t('empty.title')}</p>
                  <p className="text-sm">{t('empty.subtitle')}</p>
                  <div className="mt-4 max-w-md rounded-xl bg-surface px-4 py-3 text-left text-sm ring-1 ring-border">
                    <p className="mb-1 font-semibold text-fg">{t('about.title')}</p>
                    <p className="leading-relaxed text-muted">{t('about.etymology')}</p>
                  </div>
                </div>
              )
            ) : (
              <Thread items={items} isAdmin={isAdmin} onComplete={completeFromChat} onDeepen={deepen} onFeedback={feedback} />
            )}
          </main>

          <footer className="mx-auto w-full max-w-3xl py-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="inline-flex rounded-xl bg-surface-2 p-1 text-sm">
                {(['ask', 'capture'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    aria-pressed={mode === m}
                    className={`rounded-lg px-4 py-1.5 font-medium transition ${
                      mode === m ? 'bg-accent text-accent-fg shadow-sm' : 'text-muted hover:text-fg'
                    }`}
                  >
                    {t(`mode.${m}`)}
                  </button>
                ))}
              </div>
              {items.length > 0 && (
                <button
                  onClick={() => setItems([])}
                  title={t('chat.clear')}
                  aria-label={t('chat.clear')}
                  className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-muted hover:bg-surface-2 hover:text-fg"
                >
                  <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">{t('chat.clear')}</span>
                </button>
              )}
            </div>
            {mode === 'capture' && (
              <MyCaptures
                onEdit={startEdit}
                onDeleted={(id) => setEditing((e) => (e?.id === id ? null : e))}
                refreshKey={minesKey}
              />
            )}
            {mode === 'capture' && gapHint && !editing && (
              <div className="mb-2 flex items-center gap-2 rounded-lg bg-accent/10 px-3 py-1.5 text-xs text-accent">
                <Sparkles className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 flex-1">{gapHint}</span>
                <button onClick={() => setGapHint(null)} className="shrink-0 hover:opacity-70" aria-label={t('captureForm.removeTheme')}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {mode === 'capture' && editing && (
              <div className="mb-2 flex items-center gap-2 rounded-lg bg-accent/10 px-3 py-1.5 text-xs text-accent">
                <span className="min-w-0 flex-1 truncate">{t('capture.editing', { title: editing.title })}</span>
                <button onClick={cancelEdit} className="flex shrink-0 items-center gap-1 hover:opacity-70">
                  <X className="h-3.5 w-3.5" /> {t('capture.cancelEdit')}
                </button>
              </div>
            )}
            {mode === 'capture' && (
              <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-muted">{t('captureForm.anglesLabel')}</span>
                {angles.map((a) => (
                  <button
                    key={a}
                    onClick={() => insertAngle(t(`captureForm.${a}`))}
                    className="rounded-full bg-surface-2 px-2 py-0.5 text-muted transition hover:bg-accent hover:text-accent-fg"
                  >
                    + {t(`captureForm.${a}`)}
                  </button>
                ))}
              </div>
            )}
            <div className="rounded-2xl bg-surface p-2 ring-1 ring-border focus-within:ring-accent">
              {mode === 'capture' && (
                <div className="mb-2 border-b border-border pb-2">
                  <div className="mb-1 flex items-center gap-2 text-xs">
                    <span className="font-medium text-fg">{t('captureForm.themesLabel')}</span>
                    <span className="text-muted">· {t('captureForm.optional')}</span>
                  </div>
                  <ThemePicker value={themes} onChange={setThemes} />
                </div>
              )}
              {mode === 'capture' && files.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 rounded-lg bg-surface-2 px-2 py-1 text-xs">
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted" />
                      <span className="max-w-[12rem] truncate">{f.name}</span>
                      <span className="text-muted">{fmtSize(f.size)}</span>
                      <button onClick={() => removeFile(i)} aria-label={t('composer.removeFile')} className="hover:opacity-70">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={composerRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={mode === 'capture' ? 6 : 1}
                  placeholder={mode === 'ask' ? t('composer.askPlaceholder') : t('composer.capturePlaceholder')}
                  className={`flex-1 resize-none bg-transparent px-2 py-1.5 outline-none placeholder:text-muted ${
                    mode === 'ask'
                      ? 'max-h-40'
                      : expanded
                        ? 'max-h-[80vh] min-h-[70vh]'
                        : 'max-h-[50vh] min-h-[7rem]'
                  }`}
                />
                {mode === 'capture' && (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,image/*"
                      className="hidden"
                      onChange={(e) => addFiles(e.target.files)}
                    />
                    <button
                      onClick={() => void translateDraft()}
                      disabled={translatingDraft || !input.trim()}
                      title={t('composer.translate')}
                      aria-label={t('composer.translate')}
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-2 text-muted hover:text-fg disabled:opacity-40"
                    >
                      {translatingDraft ? (
                        <Loader2 className="h-[18px] w-[18px] animate-spin" />
                      ) : (
                        <Languages className="h-[18px] w-[18px]" />
                      )}
                    </button>
                    <button
                      onClick={() => fileRef.current?.click()}
                      title={t('composer.attach')}
                      aria-label={t('composer.attach')}
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-2 text-muted hover:text-fg"
                    >
                      <Paperclip className="h-[18px] w-[18px]" />
                    </button>
                    <button
                      onClick={() => setExpanded((v) => !v)}
                      title={expanded ? t('composer.shrink') : t('composer.expand')}
                      aria-label={expanded ? t('composer.shrink') : t('composer.expand')}
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-2 text-muted hover:text-fg"
                    >
                      {expanded ? <Minimize2 className="h-[18px] w-[18px]" /> : <Maximize2 className="h-[18px] w-[18px]" />}
                    </button>
                  </>
                )}
                <button
                  onClick={() => void submit()}
                  disabled={busy || !input.trim()}
                  className="shrink-0 rounded-xl bg-accent px-4 py-2 font-medium text-accent-fg disabled:opacity-40"
                >
                  {busy
                    ? t('composer.sending')
                    : mode === 'ask'
                      ? t('composer.send')
                      : editing
                        ? t('capture.update')
                        : t('composer.capture')}
                </button>
              </div>
            </div>
            <p className="mt-1.5 px-1 text-xs text-muted">
              {mode === 'ask' ? t('composer.hintAsk') : t('composer.hintCapture')}
            </p>
          </footer>
        </>
      )}

      {help && <Help onClose={() => setHelp(false)} onShowDisclaimer={() => { setHelp(false); setDisclaimer(true); }} />}
      {disclaimer && <Disclaimer onClose={closeDisclaimer} />}
      {insightsOpen && <Insights onClose={() => setInsightsOpen(false)} />}
      {accessOpen && <AccessSettings meId={meId} onClose={() => setAccessOpen(false)} />}
      {importOpen && <ImportConsole onClose={() => setImportOpen(false)} />}
    </div>
  );
}
